import { CAMERA } from "../config/cosmos.config.ts";

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

interface Flight { fromX: number; fromY: number; fromZ: number; toX: number; toY: number; toZ: number; t0: number; dur: number; }

/**
 * Câmera cinematográfica: zoom contínuo com easing, pan com inércia,
 * foco com transição suave. Mapeia mundo <-> tela.
 * O engine aplica (x,y,z) ao container "world" do Pixi.
 */
export class Camera {
  x = 0; y = 0; z = 1;
  private zTarget = 1;
  private velX = 0; private velY = 0;
  private flight: Flight | null = null;
  dragging = false;

  constructor(public viewW: number, public viewH: number) {}

  resize(w: number, h: number) { this.viewW = w; this.viewH = h; }

  fit(maxRadius: number) {
    this.zTarget = Math.min(this.viewW, this.viewH) / (maxRadius * CAMERA.fitPadding);
    this.flyTo(0, 0, this.zTarget, 900);
    this.velX = this.velY = 0;
  }

  /** voo de câmera cinematográfico (ease-in-out) até (x,y,zoom) */
  flyTo(x: number, y: number, zoom: number, dur = 850) {
    const z = clamp(zoom, CAMERA.zoomMin, CAMERA.zoomMax);
    this.zTarget = z;
    this.flight = { fromX: this.x, fromY: this.y, fromZ: this.z, toX: x, toY: y, toZ: z, t0: performance.now(), dur };
    this.velX = this.velY = 0;
  }
  cancelFlight() { this.flight = null; }
  get flying() { return this.flight != null; }

  focusOn(x: number, y: number, targetZoom?: number) {
    this.flyTo(x, y, targetZoom ?? this.zTarget);
  }

  zoomAt(screenX: number, screenY: number, factor: number) {
    this.flight = null;
    const [wx, wy] = this.screenToWorld(screenX, screenY);
    this.zTarget = clamp(this.zTarget * factor, CAMERA.zoomMin, CAMERA.zoomMax);
    this.x += (wx - this.x) * (1 - 1 / factor) * 0.5;
    this.y += (wy - this.y) * (1 - 1 / factor) * 0.5;
  }

  panBy(dxScreen: number, dyScreen: number) {
    this.flight = null;
    const dx = dxScreen / this.z, dy = dyScreen / this.z;
    this.x -= dx; this.y -= dy;
    this.velX = -dx; this.velY = -dy;
  }

  nudgeZoom(factor: number) { this.flight = null; this.zTarget = clamp(this.zTarget * factor, CAMERA.zoomMin, CAMERA.zoomMax); }

  /** integra física a cada frame */
  update() {
    if (this.flight) {
      const f = this.flight;
      const k = easeInOut(Math.min(1, (performance.now() - f.t0) / f.dur));
      this.x = lerp(f.fromX, f.toX, k);
      this.y = lerp(f.fromY, f.toY, k);
      this.z = lerp(f.fromZ, f.toZ, k);
      if (k >= 1) this.flight = null;
      return;
    }
    this.z = lerp(this.z, this.zTarget, CAMERA.zoomEase);
    if (!this.dragging) {
      this.x += this.velX; this.y += this.velY;
      this.velX *= CAMERA.inertiaFriction; this.velY *= CAMERA.inertiaFriction;
      if (Math.abs(this.velX) < 1e-4) this.velX = 0;
      if (Math.abs(this.velY) < 1e-4) this.velY = 0;
    }
  }

  worldToScreen(x: number, y: number): [number, number] {
    return [this.viewW / 2 + (x - this.x) * this.z, this.viewH / 2 + (y - this.y) * this.z];
  }
  screenToWorld(sx: number, sy: number): [number, number] {
    return [(sx - this.viewW / 2) / this.z + this.x, (sy - this.viewH / 2) / this.z + this.y];
  }
}
